import React, { useRef, useEffect, useState } from 'react'
import MessageList from './MessageList'
import InputArea from './InputArea'

// Context header removed - using smart auto-detection instead of locked context

// Skeleton loader for chat
function ChatSkeleton() {
  return (
    <div className="w-full max-w-[720px] mx-auto py-8 px-4 space-y-6 animate-pulse">
      {/* User message skeleton */}
      <div className="flex justify-end">
        <div className="w-48 h-10 bg-gray-200 rounded-[20px]" />
      </div>
      {/* AI message skeleton */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-16 h-4 bg-gray-200 rounded" />
          <div className="w-20 h-4 bg-gray-100 rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-5/6" />
          <div className="h-4 bg-gray-200 rounded w-4/6" />
          <div className="h-4 bg-gray-200 rounded w-3/6" />
        </div>
        <div className="flex gap-2 pt-2">
          <div className="w-24 h-7 bg-gray-100 rounded-full" />
          <div className="w-28 h-7 bg-gray-100 rounded-full" />
          <div className="w-20 h-7 bg-gray-100 rounded-full" />
        </div>
      </div>
    </div>
  )
}

// Detect question type from text for appropriate thinking steps
function detectQuestionType(question) {
  if (!question) return 'general'
  const q = question.toLowerCase()
  
  // Recap/historical questions
  if (q.includes('last night') || q.includes('yesterday') || q.includes('recap') || 
      q.includes('how did') || q.includes('what happened') || q.includes('final score')) {
    return 'recap'
  }
  
  // Betting/props questions
  if (q.includes('spread') || q.includes('over') || q.includes('under') || 
      q.includes('prop') || q.includes('bet') || q.includes('odds') || q.includes('cover')) {
    return 'betting'
  }
  
  // Player-specific questions
  if (q.includes('points') || q.includes('rebounds') || q.includes('assists') ||
      q.includes('stats') || q.includes('playing') || q.includes('injured')) {
    return 'player'
  }
  
  // Live game questions
  if (q.includes('score') || q.includes('winning') || q.includes('quarter') ||
      q.includes('right now') || q.includes('currently') || q.includes('live')) {
    return 'live'
  }
  
  return 'general'
}

// Get thinking steps based on question type
function getThinkingSteps(questionType) {
  switch (questionType) {
    case 'recap':
      return [
        'Reading your question...',
        'Checking archived game data...',
        'Pulling final box score...',
        'Building recap...'
      ]
    case 'betting':
      return [
        'Reading your question...',
        'Checking current lines...',
        'Analyzing edge factors...',
        'Forming recommendation...'
      ]
    case 'player':
      return [
        'Reading your question...',
        'Finding player data...',
        'Checking recent performance...',
        'Forming response...'
      ]
    case 'live':
      return [
        'Reading your question...',
        'Fetching live scores...',
        'Analyzing game flow...',
        'Forming response...'
      ]
    default:
      return [
        'Reading your question...',
        'Gathering context...',
        'Analyzing data...',
        'Forming response...'
      ]
  }
}

// Subtle thinking animation - ChatGPT style (inline, toggleable)
function ThinkingAnimation({ pendingQuestion }) {
  const [expanded, setExpanded] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  
  const questionType = detectQuestionType(pendingQuestion)
  const thinkingSteps = getThinkingSteps(questionType)

  useEffect(() => {
    setCurrentStep(0)
    
    // Progress through steps
    const stepTimer = setInterval(() => {
      setCurrentStep(prev => prev < thinkingSteps.length - 1 ? prev + 1 : prev)
    }, 600)
    
    return () => clearInterval(stepTimer)
  }, [pendingQuestion])

  return (
    <div className="flex items-center gap-2">
      {/* Simple pulsing dot + current step inline */}
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></span>
        <span className="text-[13px] text-gray-500">
          {thinkingSteps[currentStep] || 'Thinking...'}
        </span>
      </div>
      
      {/* Toggle to see all steps */}
      {thinkingSteps.length > 1 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[11px] text-gray-400 hover:text-gray-500 ml-2"
        >
          {expanded ? '−' : '+'}
        </button>
      )}
      
      {/* Expanded steps - only if toggled */}
      {expanded && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-sm p-2 text-[12px] text-gray-500 space-y-0.5 z-10">
          {thinkingSteps.slice(0, currentStep + 1).map((step, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="text-gray-300">→</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ChatWindow({ messages, onSendMessage, onStopGeneration, onToggleSidebar, onToggleRightSidebar, isLoading, sidebarOpen, rightSidebarOpen, pendingQuestion, dataFreshness }) {
  const messagesEndRef = useRef(null)
  const thinkingRef = useRef(null)
  const inputRef = useRef(null)

  const handlePromptSelect = (promptText, type) => {
    if (type === 'starter' && inputRef.current) {
      inputRef.current.setStarterPrompt(promptText)
    }
  }

  useEffect(() => {
    if (isLoading) {
      thinkingRef.current?.scrollIntoView({ behavior: 'smooth' })
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading])
  
  // Format freshness for display
  const getFreshnessLabel = () => {
    if (!dataFreshness || dataFreshness <= 0) return null
    if (dataFreshness < 60) return 'Live'
    if (dataFreshness < 120) return `${Math.round(dataFreshness / 60)}m ago`
    return `${Math.round(dataFreshness / 60)}m old`
  }
  const freshnessLabel = getFreshnessLabel()

  return (
    <div 
      className="flex-1 flex flex-col h-full transition-all duration-200" 
      style={{ backgroundColor: '#FAFAFA' }}
    >
      {/* Mobile Header - Fixed top bar with toggles (< 768px) - hide when sidebars open */}
      {!sidebarOpen && !rightSidebarOpen && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 md:hidden flex items-center justify-between px-3 bg-white/80 backdrop-blur-sm border-b border-gray-100" 
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', minHeight: '56px' }}
        >
          {/* Left toggle - Chat history */}
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg transition-colors hover:bg-gray-100 flex items-center justify-center"
            style={{ color: '#6B7280' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
            </svg>
          </button>
          
          {/* Center - Logo/Title + Freshness */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-600">Orb</span>
            {freshnessLabel && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                dataFreshness < 60 ? 'bg-emerald-100 text-emerald-600' :
                dataFreshness < 180 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'
              }`}>
                {freshnessLabel}
              </span>
            )}
          </div>
          
          {/* Right toggle - Games/Stats */}
          <button
            onClick={onToggleRightSidebar}
            className="p-2 rounded-lg transition-colors hover:bg-gray-100 flex items-center justify-center"
            style={{ color: '#6B7280' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Spacer for fixed header on mobile (< 768px) - only when header visible */}
      {!sidebarOpen && !rightSidebarOpen && (
        <div className="md:hidden" style={{ height: 'calc(56px + env(safe-area-inset-top))' }} />
      )}

      {/* Messages area - perfectly centered */}
      <div 
        className="flex-1 flex flex-col w-full overflow-y-auto"
        style={{ overflowY: messages.length === 0 ? 'hidden' : 'auto' }}
      >
        <div className="flex-1 flex flex-col w-full">
          <div className="flex-1 flex flex-col items-center justify-center w-full px-4 sm:px-8 lg:px-12">
            <div className="w-full max-w-[720px]">
              {messages.length === 0 && !isLoading ? (
                <div className="text-center animate-fade-in py-8" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                  <h1 
                    className="text-[28px] sm:text-[32px] mb-2"
                    style={{ 
                      color: '#1F2937', 
                      fontWeight: '600',
                      letterSpacing: '-0.02em',
                      margin: '0'
                    }}
                  >
                    Orb
                  </h1>
                  <p 
                    className="text-[13px]"
                    style={{ 
                      color: '#9CA3AF',
                      fontWeight: '500',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      margin: '0'
                    }}
                  >
                    Sports
                  </p>
                </div>
              ) : (
                <div className="w-full py-4">
                  <MessageList messages={messages} onChipClick={onSendMessage} />
                  
                </div>
              )}
              <div ref={messagesEndRef} className="w-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Input Area */}
      <InputArea 
        ref={inputRef} 
        onSendMessage={onSendMessage}
        onStopGeneration={onStopGeneration}
        isLoading={isLoading}
        onPromptSelect={handlePromptSelect}
        hasMessages={messages.length > 0}
      />
    </div>
  )
}
